import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutomationRuleCreateDto, AutomationRuleDto, automationRuleService } from '../services/automationRuleService';
import { catalogService, ConditionCatalogDto, TriggerEvent } from '../services/catalogService';
import { AutomationRule, RuleFilter, RuleFilterGroup, RuleUpdate, RuleUpdateGroup } from '../types';

export interface ConditionField {
  id: string;
  label: string;
  type: 'text' | 'select';
  options?: string[];
}

const STORAGE_KEY = 'soar_new_automation_rule_editing';

export interface NewAutomationRuleState {
  editingRuleId: string | null;
  name: string;
  description: string;
  entityType: string;
  triggerType: string;
  selectedPlaybookIds: string[];
  isActive: boolean;
  filterGroups: RuleFilterGroup[];
  updateGroups: RuleUpdateGroup[];
  events: TriggerEvent[];
  conditions: ConditionCatalogDto[];
  isLoadingEvents: boolean;
  isLoadingConditions: boolean;
  isSaving: boolean;
}

export const useNewAutomationRule = (onRuleSaved?: (rule: AutomationRule, isNew: boolean) => void) => {
  const initialState: NewAutomationRuleState = {
    editingRuleId: null,
    name: '',
    description: '',
    entityType: '',
    triggerType: '',
    selectedPlaybookIds: [],
    isActive: true,
    filterGroups: [],
    updateGroups: [],
    events: [],
    conditions: [],
    isLoadingEvents: false,
    isLoadingConditions: false,
    isSaving: false,
  };

  const [state, setState] = useState<NewAutomationRuleState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return { ...initialState, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Failed to load new rule state', e);
      }
    }
    return initialState;
  });

  const persist = useCallback((newState: Partial<NewAutomationRuleState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      const toPersist = {
        editingRuleId: updated.editingRuleId,
        name: updated.name,
        description: updated.description,
        entityType: updated.entityType,
        triggerType: updated.triggerType,
        selectedPlaybookIds: updated.selectedPlaybookIds,
        isActive: updated.isActive,
        filterGroups: updated.filterGroups,
        updateGroups: updated.updateGroups,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
      return updated;
    });
  }, []);

  const fetchEvents = useCallback(async (entityId: string) => {
    if (!entityId) return;
    setState(prev => ({ ...prev, isLoadingEvents: true }));
    try {
      const data = await catalogService.getEvents(entityId);
      setState(prev => ({ ...prev, events: Array.isArray(data) ? data : [], isLoadingEvents: false }));
    } catch (error) {
      console.error(`Failed to fetch events for entity ${entityId}`, error);
      setState(prev => ({ ...prev, isLoadingEvents: false }));
    }
  }, []);

  const fetchConditions = useCallback(async (entityId: string) => {
    if (!entityId) return;
    setState(prev => ({ ...prev, isLoadingConditions: true }));
    try {
      const data = await catalogService.getConditions(entityId);
      setState(prev => ({ ...prev, conditions: Array.isArray(data) ? data : [], isLoadingConditions: false }));
    } catch (error) {
      console.error(`Failed to fetch conditions for entity ${entityId}`, error);
      setState(prev => ({ ...prev, isLoadingConditions: false }));
    }
  }, []);

  useEffect(() => {
    if (state.entityType) {
      fetchEvents(state.entityType);
      fetchConditions(state.entityType);
    }
  }, [state.entityType, fetchEvents, fetchConditions]);

  const setName = useCallback((name: string) => persist({ name }), [persist]);
  const setIsActive = useCallback((isActive: boolean) => persist({ isActive }), [persist]);
  const setDescription = useCallback((description: string) => persist({ description }), [persist]);

  const setEntityType = useCallback(
    (entityType: string) => {
      const filterId = Math.random().toString(36).substr(2, 9);
      const updateId = Math.random().toString(36).substr(2, 9);

      persist({
        entityType,
        triggerType: '',
        filterGroups: [
          {
            id: filterId,
            filters: [
              {
                id: Math.random().toString(36).substr(2, 9),
                field: '',
                operator: 'equals',
                value: '',
              },
            ],
          },
        ],
        updateGroups: [
          {
            id: updateId,
            updates: [
              {
                id: Math.random().toString(36).substr(2, 9),
                field: '',
                from: '',
                to: '',
              },
            ],
          },
        ],
      });
    },
    [persist]
  );

  const setTriggerType = useCallback((triggerType: string) => persist({ triggerType }), [persist]);

  const togglePlaybook = useCallback(
    async (playbookId: string) => {
      const isSelected = state.selectedPlaybookIds.includes(playbookId);
      try {
        if (state.editingRuleId) {
          if (isSelected) {
            await automationRuleService.removePlaybookFromRule(state.editingRuleId, playbookId);
          } else {
            await automationRuleService.addPlaybookToRule(state.editingRuleId, playbookId);
          }
        }

        persist({
          selectedPlaybookIds: isSelected
            ? state.selectedPlaybookIds.filter(id => id !== playbookId)
            : [...state.selectedPlaybookIds, playbookId],
        });
      } catch (error) {
        console.error('Failed to update playbook association', error);
      }
    },
    [state.editingRuleId, state.selectedPlaybookIds, persist]
  );

  const removePlaybook = useCallback(
    async (playbookId: string) => {
      try {
        if (state.editingRuleId) {
          await automationRuleService.removePlaybookFromRule(state.editingRuleId, playbookId);
        }
        persist({
          selectedPlaybookIds: state.selectedPlaybookIds.filter(id => id !== playbookId),
        });
      } catch (error) {
        console.error('Failed to remove playbook association', error);
      }
    },
    [state.editingRuleId, state.selectedPlaybookIds, persist]
  );

  const reorderPlaybooks = useCallback(
    (startIndex: number, endIndex: number) => {
      const result = Array.from(state.selectedPlaybookIds);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      persist({ selectedPlaybookIds: result });
    },
    [state.selectedPlaybookIds, persist]
  );

  const addFilterGroup = useCallback(() => {
    persist({
      filterGroups: [
        ...state.filterGroups,
        {
          id: Math.random().toString(36).substr(2, 9),
          filters: [
            {
              id: Math.random().toString(36).substr(2, 9),
              field: '',
              operator: 'equals',
              value: '',
            },
          ],
        },
      ],
    });
  }, [state.filterGroups, persist]);

  const addFilterToGroup = useCallback(
    (groupId: string) => {
      persist({
        filterGroups: state.filterGroups.map(group =>
          group.id === groupId
            ? {
                ...group,
                filters: [
                  ...group.filters,
                  {
                    id: Math.random().toString(36).substr(2, 9),
                    field: '',
                    operator: 'equals',
                    value: '',
                  },
                ],
              }
            : group
        ),
      });
    },
    [state.filterGroups, persist]
  );

  const updateFilter = useCallback(
    (groupId: string, filterId: string, updates: Partial<RuleFilter>) => {
      persist({
        filterGroups: state.filterGroups.map(group =>
          group.id === groupId ? { ...group, filters: group.filters.map(f => (f.id === filterId ? { ...f, ...updates } : f)) } : group
        ),
      });
    },
    [state.filterGroups, persist]
  );

  const removeFilter = useCallback(
    (groupId: string, filterId: string) => {
      const updatedGroups = state.filterGroups
        .map(group => (group.id === groupId ? { ...group, filters: group.filters.filter(f => f.id !== filterId) } : group))
        .filter(group => group.filters.length > 0);

      persist({ filterGroups: updatedGroups });
    },
    [state.filterGroups, persist]
  );

  const addUpdateGroup = useCallback(() => {
    persist({
      updateGroups: [
        ...state.updateGroups,
        {
          id: Math.random().toString(36).substr(2, 9),
          updates: [
            {
              id: Math.random().toString(36).substr(2, 9),
              field: '',
              from: '',
              to: '',
            },
          ],
        },
      ],
    });
  }, [state.updateGroups, persist]);

  const addUpdateToGroup = useCallback(
    (groupId: string) => {
      persist({
        updateGroups: state.updateGroups.map(group =>
          group.id === groupId
            ? {
                ...group,
                updates: [
                  ...group.updates,
                  {
                    id: Math.random().toString(36).substr(2, 9),
                    field: '',
                    from: '',
                    to: '',
                  },
                ],
              }
            : group
        ),
      });
    },
    [state.updateGroups, persist]
  );

  const updateRuleUpdate = useCallback(
    (groupId: string, updateId: string, updates: Partial<RuleUpdate>) => {
      persist({
        updateGroups: state.updateGroups.map(group =>
          group.id === groupId ? { ...group, updates: group.updates.map(u => (u.id === updateId ? { ...u, ...updates } : u)) } : group
        ),
      });
    },
    [state.updateGroups, persist]
  );

  const removeUpdate = useCallback(
    (groupId: string, updateId: string) => {
      const updatedGroups = state.updateGroups
        .map(group => (group.id === groupId ? { ...group, updates: group.updates.filter(u => u.id !== updateId) } : group))
        .filter(group => group.updates.length > 0);

      persist({ updateGroups: updatedGroups });
    },
    [state.updateGroups, persist]
  );

  const resetForm = useCallback(() => {
    const filterId = Math.random().toString(36).substr(2, 9);
    const updateId = Math.random().toString(36).substr(2, 9);

    const resetState: Partial<NewAutomationRuleState> = {
      editingRuleId: null,
      name: '',
      description: '',
      entityType: '',
      triggerType: '',
      selectedPlaybookIds: [],
      isActive: true,
      filterGroups: [
        {
          id: filterId,
          filters: [
            {
              id: Math.random().toString(36).substr(2, 9),
              field: '',
              operator: 'equals',
              value: '',
            },
          ],
        },
      ],
      updateGroups: [
        {
          id: updateId,
          updates: [
            {
              id: Math.random().toString(36).substr(2, 9),
              field: '',
              from: '',
              to: '',
            },
          ],
        },
      ],
      events: [],
      conditions: [],
    };
    persist(resetState);
  }, [persist]);

  const loadRule = useCallback(
    (rule: AutomationRule) => {
      persist({
        editingRuleId: rule.id,
        name: rule.name || '',
        description: rule.description || '',
        entityType: rule.entityType || '',
        triggerType: rule.triggerType || '',
        selectedPlaybookIds: [...(rule.playbookIds || [])],
        isActive: rule.status === 'Active',
        filterGroups:
          rule.filterGroups && rule.filterGroups.length > 0
            ? JSON.parse(JSON.stringify(rule.filterGroups))
            : [
                {
                  id: Math.random().toString(36).substr(2, 9),
                  filters: [{ id: Math.random().toString(36).substr(2, 9), field: '', operator: 'equals', value: '' }],
                },
              ],
        updateGroups:
          rule.updateGroups && rule.updateGroups.length > 0
            ? JSON.parse(JSON.stringify(rule.updateGroups))
            : [
                {
                  id: Math.random().toString(36).substr(2, 9),
                  updates: [{ id: Math.random().toString(36).substr(2, 9), field: '', from: '', to: '' }],
                },
              ],
      });
    },
    [persist]
  );

  const isValid = useMemo(
    () =>
      (state.name?.trim()?.length || 0) > 0 && (state.entityType?.trim()?.length || 0) > 0 && (state.triggerType?.trim()?.length || 0) > 0,
    [state.name, state.entityType, state.triggerType]
  );

  const saveAutomationRule = useCallback(async () => {
    if (!isValid) return null;

    setState(prev => ({ ...prev, isSaving: true }));

    const ruleData: AutomationRuleCreateDto = {
      name: state.name,
      description: state.description,
      active: state.isActive,
      triggerEntity: state.entityType,
      triggerEvent: state.triggerType,
      playbookIds: [...state.selectedPlaybookIds],
      filter: {
        filterGroups: state.filterGroups,
        updateGroups: state.updateGroups,
      },
    };

    try {
      const isNew = !state.editingRuleId;
      const savedRule: AutomationRule = await (async () => {
        if (state.editingRuleId) {
          const updateDto: Partial<AutomationRuleDto> = {
            name: state.name,
            description: state.description,
            active: state.isActive,
            triggerEntity: state.entityType,
            triggerEvent: state.triggerType,
            filter: ruleData.filter,
          };
          await automationRuleService.updateAutomationRule(state.editingRuleId, updateDto);

          return {
            id: state.editingRuleId,
            name: state.name,
            description: state.description,
            status: state.isActive ? 'Active' : 'Inactive',
            lastModified: new Date().toISOString(),
            entityType: state.entityType,
            triggerType: state.triggerType,
            playbookIds: [...state.selectedPlaybookIds],
            filterGroups: state.filterGroups,
            updateGroups: state.updateGroups,
          };
        }
        const createdRule = await automationRuleService.createAutomationRule(ruleData);

        return {
          id: createdRule.ruleId,
          name: createdRule.name,
          description: createdRule.description,
          status: createdRule.active ? 'Active' : 'Inactive',
          lastModified: createdRule.updatedAt || createdRule.createdAt || new Date().toISOString(),
          entityType: createdRule.triggerEntity,
          triggerType: createdRule.triggerEvent,
          playbookIds: createdRule.playbooks?.map(p => p.playbookId) || [],
          filterGroups: state.filterGroups,
          updateGroups: state.updateGroups,
        };
      })();

      if (onRuleSaved) {
        onRuleSaved(savedRule, isNew);
      }

      setState(prev => ({ ...prev, isSaving: false }));
      return savedRule;
    } catch (error) {
      console.error('Failed to save automation rule', error);
      setState(prev => ({ ...prev, isSaving: false }));
      return null;
    }
  }, [isValid, state, onRuleSaved]);

  const triggerOptions = useMemo(() => state.events.map(e => ({ title: e.eventName || e.eventId, value: e.eventId })), [state.events]);

  const availableConditionFields = useMemo(
    (): ConditionField[] =>
      state.conditions.map(c => ({
        id: c.fieldKey,
        label: c.fieldKey.split('.').pop() || c.fieldKey,
        type: c.fieldType.toLowerCase() === 'enum' ? 'select' : 'text',
      })),
    [state.conditions]
  );

  return {
    ...state,
    isValid,
    triggerOptions,
    availableConditionFields,
    setName,
    setIsActive,
    setDescription,
    setEntityType,
    setTriggerType,
    togglePlaybook,
    removePlaybook,
    reorderPlaybooks,
    addFilterGroup,
    addFilterToGroup,
    updateFilter,
    removeFilter,
    addUpdateGroup,
    addUpdateToGroup,
    updateRuleUpdate,
    removeUpdate,
    saveAutomationRule,
    loadRule,
    resetForm,
  };
};
